import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useRangeQuery } from "@/lib/useData";
import { api } from "@/lib/api";
import {
  PageTitle, Card, SectionTitle, Takeaway, LoadingBlocks, ErrorState, Forbidden, EmptyState,
} from "@/components/shared/primitives";
import { AdoptionChart } from "@/components/shared/AdoptionChart";
import { Avatar } from "@/components/shared/Avatar";

const PRIORITY = { High: "var(--neg-chart)", Medium: "var(--brand-floral)", Low: "var(--text-muted)" };
const STATUS_DOT = { Open: "var(--brand-kawaii)", "In progress": "var(--brand-floral)", Resolved: "var(--text-muted)", Closed: "var(--border-control)", Pending: "var(--brand-floral)", Completed: "var(--text-muted)", Overdue: "var(--neg-chart)" };

function Chip({ text, dot }) {
  return <span className="inline-flex items-center gap-1.5 text-[12.5px] text-nb-ink rounded-md px-2 py-0.5" style={{ background: "var(--bg-sunken)" }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />{text}</span>;
}

const TABS = [
  { key: "grievances", label: "Grievances" },
  { key: "assigned", label: "Assigned" },
  { key: "completed", label: "Completed" },
  { key: "responses", label: "Responses" },
];

export default function MemberProfile() {
  const { id } = useParams();
  const { data, isLoading, error, refetch } = useRangeQuery(`member-${id}`, `/members/${id}`);
  const [activity, setActivity] = useState(null);
  const [tab, setTab] = useState("grievances");
  useEffect(() => { api.get(`/members/${id}/activity`).then((r) => setActivity(r.data)).catch(() => setActivity({ grievances: [], assigned: [], completed: [], responses: [] })); }, [id]);

  if (error?.code === "forbidden") return <Forbidden who="Admins and the KM team (Partners see their own team)" />;
  if (isLoading) return <LoadingBlocks rows={4} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const m = data.data.member;
  const tools = data.data.tools || [];
  const toolsUsed = tools.filter((t) => t.used).length;
  const hours = Math.round((data.data.activity || []).reduce((s, w) => s + w.active_minutes, 0) / 60);
  const completed = activity?.completed?.length ?? 0;

  const kpis = [
    { label: "Tools in active use", value: toolsUsed },
    { label: "Tools assigned", value: tools.filter((t) => t.assigned).length },
    { label: "Hours of AI use (12wk)", value: hours },
    { label: "Questionnaires done", value: completed },
  ];

  return (
    <div>
      <Link to="/people" className="inline-flex items-center gap-1.5 text-[13px] text-nb-ink2 hover:text-nb-ink mb-3 transition-colors">
        <ArrowLeft size={14} /> All members
      </Link>

      <Card className="p-6 mb-6" data-testid="member-profile-page">
        <div className="flex items-center gap-4">
          <Avatar photoPath={m.photo_path} name={m.name} size={64} testid="member-avatar" />
          <div>
            <h1 className="font-display text-[24px] font-bold text-nb-ink leading-tight">{m.name}</h1>
            <p className="text-[14px] text-nb-ink2">
              {[m.designation, m.seniority, m.team, m.location].filter(Boolean).join(" · ")}
            </p>
            <p className="text-[13px] text-nb-ink2">{m.email}{m.practice_area ? ` · ${m.practice_area}` : ""}</p>
          </div>
        </div>
      </Card>

      <Card className="p-4 mb-6 flex items-start gap-3" style={{ backgroundColor: "rgba(146,186,213,0.22)" }} data-testid="transparency-note">
        <ShieldCheck size={18} strokeWidth={1.5} className="text-nb-ink shrink-0 mt-0.5" />
        <p className="text-[13px] text-nb-ink">We track which tools and features are used, never the content of prompts, documents or results.</p>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => (
          <Card key={k.label} className="p-5">
            <div className="nb-label mb-1">{k.label}</div>
            <div className="font-display text-[28px] font-bold text-nb-ink tnum">{k.value}</div>
          </Card>
        ))}
      </div>

      <Card className="p-6 mb-6">
        <SectionTitle className="mb-1">AI adoption over time</SectionTitle>
        <Takeaway>Toggle tools in the legend, switch the metric, or change the range. Scroll to zoom, drag to pan.</Takeaway>
        <AdoptionChart series={data.data.series} />
      </Card>

      <Card className="p-5 mb-6" data-testid="tool-breakdown">
        <SectionTitle className="mb-3">Tool breakdown</SectionTitle>
        {tools.length === 0 ? <p className="text-[14px] text-nb-ink2">No tools assigned.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead><tr className="nb-label text-left" style={{ background: "var(--bg-sunken)" }}>
                <th className="py-2.5 px-3 first:rounded-l-md">Tool</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3 last:rounded-r-md">Most used features</th>
              </tr></thead>
              <tbody>
                {tools.map((t) => (
                  <tr key={t.app_id} className="border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }}>
                    <td className="py-3 px-3"><Link to={`/apps/${t.app_id}`} className="text-nb-ink font-medium hover:text-[var(--text-link)]">{t.app}</Link></td>
                    <td className="py-3 px-3"><span className="text-[12.5px] px-2 py-0.5 rounded-md text-nb-ink" style={{ background: t.used ? "var(--bg-selected)" : "var(--bg-sunken)" }}>{t.used ? "In use" : "Not yet"}</span></td>
                    <td className="py-3 px-3 text-nb-ink2">{t.top_features.map((f) => f.feature).filter(Boolean).join(", ") || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-5" data-testid="member-activity">
        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`activity-tab-${t.key}`}
              className="px-3 py-1.5 rounded-md text-[13px] font-medium"
              style={tab === t.key ? { background: "var(--brand-lime)", color: "var(--brand-navy)" } : { border: "1px solid var(--border-subtle)", color: "var(--text-muted)" }}>
              {t.label}{activity ? ` (${activity[t.key].length})` : ""}
            </button>
          ))}
        </div>
        {!activity ? <LoadingBlocks rows={2} /> : (
          <>
            {tab === "grievances" && (activity.grievances.length === 0 ? <EmptyState title="No tickets raised." /> : (
              <div className="space-y-2">{activity.grievances.map((g) => (
                <div key={g.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }}>
                  <div><div className="text-[14px] text-nb-ink">{g.subject}</div><div className="text-[12px] text-nb-ink2">{g.category} · {g.created_at?.slice(0, 10)}</div></div>
                  <div className="flex gap-2 shrink-0"><Chip text={g.priority} dot={PRIORITY[g.priority]} /><Chip text={g.status} dot={STATUS_DOT[g.status]} /></div>
                </div>
              ))}</div>
            ))}
            {tab === "assigned" && (activity.assigned.length === 0 ? <EmptyState title="Nothing assigned." /> : (
              <div className="space-y-2">{activity.assigned.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }}>
                  <Link to="/review" className="text-[14px] text-nb-ink hover:text-[var(--text-link)]">{a.title}</Link>
                  <div className="flex items-center gap-3 shrink-0"><span className="text-[12px] text-nb-ink2">{a.due_date ? `Due ${a.due_date}` : ""}</span><Chip text={a.status} dot={STATUS_DOT[a.status]} /></div>
                </div>
              ))}</div>
            ))}
            {tab === "completed" && (activity.completed.length === 0 ? <EmptyState title="Nothing completed yet." /> : (
              <div className="space-y-2">{activity.completed.map((c) => (
                <Link key={c.id} to="/review" className="block py-2 border-b last:border-0 text-[14px] text-nb-ink hover:text-[var(--text-link)]" style={{ borderColor: "var(--border-subtle)" }}>{c.title}</Link>
              ))}</div>
            ))}
            {tab === "responses" && (activity.responses.length === 0 ? <EmptyState title="No open answers." next="Anonymous questionnaire answers are never shown here." /> : (
              <div className="space-y-3">{activity.responses.map((r, i) => (
                <div key={i} className="py-2 border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }}>
                  <div className="text-[12px] text-nb-ink2">{r.questionnaire} · {r.date}</div>
                  <div className="text-[13px] text-nb-ink2 mt-0.5">{r.question}</div>
                  <div className="text-[14px] text-nb-ink">{r.answer}</div>
                </div>
              ))}</div>
            ))}
          </>
        )}
      </Card>
    </div>
  );
}
